import { runWithErrorReporting } from "$root/api/error-reporting/report";
import { useApiGen } from "$root/api/hooks";
import { useFunctionalAreas } from "$root/App/context";
import AuthorizationGuard, { hasAccess } from "$root/components/AuthorizationGuard";
import { ButtonWithDropdown } from "$root/components/buttons/ButtonWithDropdown";
import { spawnDatePickerDialog } from "$root/components/spawnable/date-picker-dialog";
import { type AsOfSnapshotState, isAsOfReadable, useAsOfSnapshot } from "./as-of/useAsOfSnapshot";
import { useCrumbs } from "$root/components/Crumbs/useCrumbs";
import { LabelRounded } from "$root/components/LabelRounded/Index";
import { PageDownloadAndMoreActionsMenu } from "$root/components/PageDownloadAndMoreActionsMenu";
import { PageHeader, type PageHeaderProps } from "$root/components/PageHeader";
import { typedUrlForRoute, useTypedNavigation } from "$root/components/PlatformRouter/RoutesDef";
import {
	EditPortfolioV4ContextProvider,
	EditPortfolioV4InContext,
} from "$root/components/Portfolio/CreatePortfolio/EditPortfolio";
import { ReactQueryWrapperBase } from "$root/components/ReactQueryWrapper";
import type { MinimumDialogProps } from "$root/components/spawnable/type";
import { spawnYesNoDialog } from "$root/components/spawnable/yes-no-dialog";
import { useEventBus, useGroupEventBus } from "$root/event-bus";
import { spawnAccessDialog } from "$root/functional-areas/acl/AccessDialog";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import EntityStatus from "$root/functional-areas/acl/EntityStatus";
import { buildCompositionExportConfig } from "$root/functional-areas/instruments/export";
import { navigateToReviewIfAnythingToReviewSync } from "$root/functional-areas/instruments/review";
import { spawnConvertToQuantitiesDialog } from "$root/functional-areas/instruments-editor/quantity/ConvertToQuantitiesDialog";
import { canConvertToQuantities } from "$root/functional-areas/instruments-editor/quantity/gate";
import { quantityConversionRepository } from "$root/functional-areas/instruments-editor/quantity/repository";
import { downloadSinglePdf, generateReportBuilderTemplateUrl } from "$root/functional-areas/pdf";
import { PortfolioStudioSettingTabEnum } from "$root/functional-areas/portfolio-studio-settings/tab-enum";
import {
	type TemplateChooserSubmitParams,
	spawnPortfolioTemplateChooser,
} from "$root/functional-areas/portfolio-studio/dialogs/TemplateChooserDialog";
import { AutoSyncStatus } from "$root/functional-areas/portfolio/AutoSyncStatus";
import { SmartXStatus } from "$root/functional-areas/portfolio/SmartXStatus";
import { usePortfolioEntityManagementActions } from "$root/functional-areas/portfolio/entity-management";
import { referenceRepository } from "$root/functional-areas/portfolio/references-repository";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { createPrimaryBenchmarkLabel } from "$root/functional-areas/reference/common";
import { downloadStoryfolioV2Document } from "$root/functional-areas/storyfolio-v2/download";
import { isDefaultReportTemplate } from "$root/functional-areas/reports/default-templates";
import {
	exportTemplateIcon,
	exportTemplateId,
	exportTemplateLabel,
	isStoryfolioV2Template,
} from "$root/functional-areas/reports/export-templates";
import { reportRepository } from "$root/functional-areas/reports/repository";
import { type IUser, user$, useUserValue } from "$root/functional-areas/user";
import { userRepository } from "$root/functional-areas/user/repository";
import { persistedColStateKeys } from "$root/functional-areas/user/userStorage";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import useCompositionDownload from "$root/hooks/useCompositionDownload";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { PaletteColors } from "$root/styles/themePalette";
import { axiosExtract } from "$root/third-party-integrations/axios";
import { generateIntentId, trackMixPanelEvent } from "$root/third-party-integrations/initMixPanel";
import { useMappedQuery, useQueryNoRefetch, useQueryReactor } from "$root/third-party-integrations/react-query/hooks";
import {
	type ContextContent,
	customObjectEntriesFn,
	downloadContentDisposition,
	filterMap,
	localDateToServerDate,
	objMatchFn,
	parallelize,
	qualifier,
	seconds,
	serverYesterdayAsLocalDate,
	ToastableError,
	useSearchParams,
	utcDateToLocalDate,
} from "$root/utils";
import { PortfolioContext } from "$root/widgets-architecture/contexts/portfolio";
import type { WidgetGridHandles } from "$root/widgets-architecture/layout/WidgetsGrid";
import WidgetsMapper from "$root/widgets-architecture/layout/WidgetsMapper";
import {
	IntegrationsControllerV3ApiFactory,
	type InvestmentActions,
	type InvestmentBenchmarkDTO,
	type InvestmentBenchmarkDTOBenchmarkTypeEnum,
	InvestmentEnhancementExportControllerApiFactory,
	InvestmentEnhancementReportsControllerApiFactory,
	InvestmentExportConverterType,
	type InvestmentStatus,
} from "@eu.mdotm.api/sphere";
import {
	AutoSortTable,
	DefaultTabTitle,
	Dialog,
	DialogFooter,
	Icon,
	type IconName,
	type Option,
	Tab,
	TabGroup,
	TableDataCell,
	Text,
	useSelectableTableColumn,
} from "@mdotm/shadcn/legacy/components";
import { type MultiSelectCtx, useSearchable } from "@mdotm/shadcn/legacy/headless";
import {
	adaptAnimatedNodeProvider,
	spawn,
	type SpawnResult,
	toClassListRecord,
	useDrivenState,
} from "@mdotm/shadcn/legacy/react-extensions";
import { atom, readAtom, useAtomValue, writeAtom } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import {
	alwaysArray,
	builtInSortFnFor,
	noop,
	type Setter,
	unpromisify,
	type Updater,
} from "@mdotm/shadcn/legacy/utils";
import {
	type DropdownDropdownItem,
	DropdownMenu,
	type DropdownTriggerProps,
	Select,
	TextInput,
	Button,
	IconButton,
	FormField,
	SubmitButton,
} from "@mdotm/shadcn/mdotm/components";
import { Sandwich } from "@mdotm/shadcn/mdotm/layout";
import { asyncStateFrom } from "@mdotm/utils/promise";
import { useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { unreachable } from "@mdotm/utils/runtime";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import * as i18next from "i18next";
import {
	Fragment,
	type RefObject,
	useCallback,
	useEffect,
	useImperativeHandle,
	useMemo,
	useRef,
	useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { match } from "ts-pattern";
import type { ReportTemplateVariant } from "../../functional-areas/reports/report-latest";
import { PortfolioStudioTab } from "../PortfoliosStudio/portfolio-studio-tabs";
import { usePortfolioStudioRedirect } from "../PortfoliosStudio/usePortfolioStudioRedirect";
import { ReviewEntity } from "../ReviewInstruments.const";
import { AsOfBar } from "./as-of/AsOfBar";
import { AsOfButton } from "./as-of/AsOfButton";
import { AS_OF_SEARCH_PARAM, parseAsOfParam } from "./as-of/asOf";
import type { InvestmentSummaryWithRequiredUuid } from "./common";
import { useResolvedSummary } from "./useResolvedSummary";
import Overview from "./Overview";
import type { PortfolioDetailsTabs } from "./portfolio-details-tabs";
import { PortfolioCommentary } from "./PortfolioCommentary";
import { PortfolioComposition } from "./PortfolioComposition";
import type { GridName } from "./types";
import { canSendToExecution } from "$root/functional-areas/portfolio/execution";

function EditPortfolioTrigger({
	children,
	state,
	...props
}: DropdownTriggerProps & { children?: React.ReactNode }): React.JSX.Element {
	return (
		<Button state={asyncStateFrom(state)} progressBar={false} size="small" palette="primary" {...props}>
			<Icon icon="Edit" size={16} />
			&nbsp;
			{children}
		</Button>
	);
}

type PageProps = {
	workspaceUid?: string;
	portfolioUid: string;
};

const PAGE_NAME = "PORTFOLIO_DETAILS";
const ENH_PAGE_NAME = "PORTFOLIO_ENHANCEMENT";

/** Live-view only: in history mode the header renders no subtitle at all (see the PageHeader call). */
function PortfolioSubtitle(props: { summary: InvestmentSummaryWithRequiredUuid; setAsOf(asOf: string | null): void }) {
	const { summary, setAsOf } = props;
	const { formatDate } = useLocaleFormatters();

	const { t } = useTranslation();

	return (
		<div className="flex items-center justify-between gap-4">
			<div className="flex items-center space-x-4 text-[#585D68] min-w-0">
				<AsOfButton
					setAsOf={setAsOf}
					inceptionDate={summary.creationDate}
					qualifier="PortfolioDetails/PageHeader/AsOfButton"
				/>
				<div
					className="flex items-center"
					data-qualifier="PortfolioDetails/PageHeader/Status"
					data-status={summary.status}
				>
					<span className="mr-2 uppercase">{t("STATUS")}</span>
					<LabelRounded content={{ status: summary.status, component: "" }} />
				</div>
				<SmartXStatus portfolioUuid={summary.uuid} />
				<div className="flex items-center space-x-2" data-qualifier="PortfolioDetails/PageHeader/Action">
					<p className="uppercase whitespace-nowrap">{t("PORTFOLIOS.PF_LAST_ACTION")} </p>
					<p className="font-bold uppercase whitespace-nowrap">
						{summary.action && summary.actualAction ? t(`PORTFOLIO_ACTION_STATUS.${summary.actualAction}`) : "-"}
					</p>
					{summary.modificationDate && <div className="whitespace-nowrap">({formatDate(summary.lastActionDate)})</div>}
				</div>
				{((summary.importedExternally && summary.syncDate) || summary.importedPerformances?.lastImportDate) && (
					<AutoSyncStatus
						variant="verbose"
						syncDate={summary.syncDate}
						lastMetricImportDate={summary.importedPerformances?.lastImportDate}
					/>
				)}
				<EntityStatus
					accessControl={summary.richAcl}
					entity="INVESTMENT"
					entityId={summary.uuid}
					entityName={summary.name}
				/>
			</div>
		</div>
	);
}

/**
 * The header while the page is pinned to a past date: a download menu carrying the visible report
 * templates and the composition xls, each pinned to that date. The integration converters are
 * absent because their endpoint takes no date parameter.
 */
function PortfolioHistoryTitleActions(props: {
	summary: InvestmentSummaryWithRequiredUuid;
	asOf: string;
	asOfSnapshot: AsOfSnapshotState;
}): React.JSX.Element | null {
	const { summary, asOf, asOfSnapshot } = props;
	const { data: templateList } = reportRepository.templates.useQuery();
	// An ungenerated snapshot is answered with an empty composition rather than an error, so an early
	// click would download a valid, empty, date-named file.
	const compositionReady = isAsOfReadable(asOf, asOfSnapshot);
	// The PDF pipeline waits for a snapshot that is still being generated, so a report may be asked
	// for meanwhile. Once that generation has failed or timed out there is nothing left to wait for,
	// and the request would only spend the backend's 90-second ceiling before erroring.
	const reportReady = asOfSnapshot.kind !== "error";

	// The same downloadable-status set the live header gates its whole group on: a portfolio the live
	// view offers no downloads for does not acquire any by being pinned to a date.
	if (!(["ACCEPTED", "READY", "PROPOSAL_READY"] satisfies InvestmentStatus[]).some((x) => x === summary.status)) {
		return null;
	}

	return (
		<PageDownloadAndMoreActionsMenu
			area="portfolio"
			downloadActions={[
				[
					"",
					[
						...filterMap(templateList ?? [], (template) =>
							!template.visible
								? []
								: [
										{
											icon: "pdf" as const,
											"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(portfolioTemplate)",
											label: template.templateName ?? "",
											disabled: !reportReady,
											onClickAsync: async () => {
												if (!reportReady) {
													return;
												}

												const [reportConf] = generateReportBuilderTemplateUrl(
													[{ name: summary.name, uuid: summary.uuid, choice: "current" }],
													template,
													{ asOf },
												);

												await runWithErrorReporting(
													async () => {
														await downloadSinglePdf(reportConf.name, reportConf);

														trackMixPanelEvent("portfolio.entityAction.downloadReport", {
															portfolio: {
																uuid: summary.uuid,
																name: summary.name!,
															},
															template: {
																uuid: isDefaultReportTemplate(template) ? template.id : template.uuid!,
																name: template.templateName!,
															},
														});
													},
													{
														area: "portfolio",
														attemptedOperation: {
															message: "download portfolio report",
															payload: JSON.stringify(reportConf),
														},
													},
												).catch(noop);
											},
										},
									],
						),
						{
							icon: "xls" as const,
							"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(composition)",
							label: "Portfolio composition",
							disabled: !compositionReady,
							onClickAsync: async () => {
								// `disabled` is enforced by a pointer-events class, not by the menu's click path, so
								// the gate is repeated where it decides the request.
								if (!compositionReady) {
									return;
								}

								const config = buildCompositionExportConfig("INVESTMENT", {
									investmentUuid: summary.uuid,
									snapshot: "CURRENT",
									colStateKey: persistedColStateKeys["tableV3-investment-composition-read"],
									compositionType: "STANDARD",
									proposalDate: summary.modificationDate,
									quantityMode: summary.quantityMode,
									asOf,
								});
								await runWithErrorReporting(
									async () => {
										const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
										await downloadContentDisposition(file);

										trackMixPanelEvent("portfolio.entityAction.downloadComposition", {
											portfolio: {
												uuid: summary.uuid,
												name: summary.name!,
											},
											format: "SPHERE_TEMPLATE_CONVERTER",
										});
									},
									{
										area: "portfolio",
										attemptedOperation: {
											message: "export portfolio composition",
											payload: JSON.stringify(config),
										},
									},
								).catch(noop);
							},
						},
					],
				] satisfies [title: string, actions: Array<DropdownDropdownItem>],
			]}
		/>
	);
}

/** Live-view only: while the page is pinned the header renders `PortfolioHistoryTitleActions`. */
function PortfolioTitleActions(props: {
	summary: InvestmentSummaryWithRequiredUuid;
	isEnhanceSelected: boolean;
	setIsEnhanceSelected: Setter<boolean>;
	entityManagementActions: ReturnType<typeof usePortfolioEntityManagementActions>;
	hasProposalWidgetsLoadedCorrectlyQuery: UseQueryResult<boolean>;
	workspaceUid?: string;
}) {
	const { entityManagementActions, isEnhanceSelected, setIsEnhanceSelected, summary } = props;
	// Through a ref: the setter's identity changes with the view, and rebuilding the action mid-flight
	// would swap the `processing$` atom the buttons are disabled from.
	const setIsEnhanceSelectedRef = useUnsafeUpdatedRef(setIsEnhanceSelected);
	const proposalAction = useMemo(
		() =>
			handleProposalAction(summary.uuid, summary.name!, {
				isExecuting: summary.status === "EXECUTING",
				leaveProposalView: () => setIsEnhanceSelectedRef.current?.(false),
			}),
		[summary.uuid, summary.name, summary.status, setIsEnhanceSelectedRef],
	);
	const processing = useAtomValue(proposalAction.processing$);
	const validateAction = validatePortfolioAction();

	const { push } = useTypedNavigation();
	const user = useUserValue();
	const { t } = useTranslation();

	const { downloadInvestmentConversion } = useCompositionDownload();
	const integrationsApi = useApiGen(IntegrationsControllerV3ApiFactory);

	const exportEnhancedApi = useApiGen(InvestmentEnhancementExportControllerApiFactory);

	const { data: templateList } = reportRepository.exportTemplates.useQuery();
	// The ROOT preview opens the in-app `Report` route, which renders a Report Builder template and
	// knows nothing of Storyfolio V2's — so it reads the unmerged list.
	const { data: previewTemplateList } = reportRepository.templates.useQuery();

	const customerQuery = useQuery({
		...userRepository.customer.getQueryConfig(user.customerId!),
		enabled:
			isEnhanceSelected &&
			hasAccess(user, {
				permissionChecker: aclByArea.portfolio.canCreateProposal,
				acl: summary.richAcl?.acl ?? [],
			}),
	});

	const { data: hasProposalWidgetsLoadedCorrectly } = props.hasProposalWidgetsLoadedCorrectlyQuery;

	return (
		<div className="flex items-center space-x-2">
			{(function SwitchPortfolioActions() {
				return match(summary)
					.when(
						(x) => x.status === "READY" || x.status === "ACCEPTED",
						() => (
							<AuthorizationGuard
								permissionChecker={aclByArea.portfolio.canCreateProposal}
								acl={summary.richAcl?.acl ?? []}
							>
								<Button
									type="button"
									palette="primary"
									size="small"
									onClick={() => {
										const intentId = generateIntentId();
										trackMixPanelEvent("portfolio.entityActions.createProposalIntent", {
											intentId,
											portfolio: {
												uuid: summary.uuid,
												name: summary.name!,
											},
										});
										if (props.workspaceUid) {
											push("Portfolios/EditPortfolioWithWorkspace", {
												workspaceUid: props.workspaceUid,
												portfolioUid: summary.uuid,
												intentId,
											});
										} else {
											push("Portfolios/EditPortfolio", { portfolioUid: summary.uuid, intentId });
										}
									}}
									className={{ "px-3!": true }}
									data-qualifier="PortfolioDetails/CreateProposal/Button"
								>
									<Icon icon="Enhance" color={PaletteColors.WHITE} size={20} />
									&nbsp;Create Proposal
								</Button>
							</AuthorizationGuard>
						),
					)
					.when(
						(x) =>
							(x.status === "PROPOSAL_READY" || x.status === "EXECUTING" || x.status === "CALCULATING") &&
							x.hasVisibleProposal &&
							!isEnhanceSelected,
						(x) =>
							x.status === "CALCULATING" && x.action === "ENHANCEMENT" ? (
								<></>
							) : (
								<Button
									size="small"
									palette="info"
									// An accept or reject in flight has already left the proposal view, and the proposal is
									// about to be deleted: re-entering here would remount the enhancement-report queries
									// just in time for the action's invalidation to refetch them.
									disabled={processing}
									onClick={() => setIsEnhanceSelected(true)}
									data-qualifier="PortfolioDetails/OpenProposal/Button"
								>
									<Icon icon="Icon-full-small" />
									&nbsp;Open Proposal
								</Button>
							),
					)
					.when(
						(x) =>
							(x.status === "PROPOSAL_READY" || x.status === "CALCULATING") &&
							x.hasVisibleProposal &&
							isEnhanceSelected,
						(x) => (
							<div className="space-x-2 flex items-center">
								<Button
									size="small"
									palette="infoOutline"
									disabled={processing}
									onClick={() => setIsEnhanceSelected(false)}
									style={{ borderColor: "#03a0dc" }}
									data-qualifier="PortfolioDetails/BackToCurrent/Button"
								>
									Back to current
								</Button>

								<AuthorizationGuard
									permissionChecker={aclByArea.portfolio.canCreateProposal}
									acl={summary.richAcl?.acl ?? []}
								>
									<ReactQueryWrapperBase query={customerQuery}>
										{(customer) => (
											<>
												{canSendToExecution(customer) && summary.liveTrading ? (
													<Button
														size="small"
														palette="info"
														onClickAsync={() => proposalAction.dispatch("execute")}
														data-qualifier="PortfolioDetails/ExecuteProposal/Button"
														disabled={processing || !hasProposalWidgetsLoadedCorrectly || x.status === "CALCULATING"}
													>
														<Icon icon="Outline" />
														&nbsp;Execute
													</Button>
												) : (
													<ButtonWithDropdown
														size="small"
														palette="info"
														disabled={processing || !hasProposalWidgetsLoadedCorrectly || x.status === "CALCULATING"}
														onClickAsync={() => proposalAction.dispatch("accept")}
														actions={[
															// Only offer "accept in the past" when there is at least one fully-closed
															// day to target — i.e. the last recorded trade is on or before the
															// server's yesterday. Otherwise omit the action entirely.
															...(summary.lastTradeDate &&
															utcDateToLocalDate(summary.lastTradeDate) <= serverYesterdayAsLocalDate()
																? [
																		{
																			label: "Accept in the past",
																			// Synchronous: opens a dialog, so the item shouldn't sit in a loading state.
																			onClick: unpromisify(async () => {
																				if (!summary?.lastTradeDate) {
																					return;
																				}
																				// Latest selectable day, shown in the user's local calendar but anchored to
																				// the server's (Europe/Rome) yesterday — the last fully-closed trading day.
																				const maxDate = serverYesterdayAsLocalDate();
																				await spawnDatePickerDialog({
																					header: "Accept in the past",
																					confirmLabel: "Accept",
																					defaultValue: maxDate,
																					// Despite the name, the backend recycles `lastTradeDate` to carry the
																					// effective earliest acceptable day, i.e. max(lastTradeDate, proposalCreation).
																					// So the real lower bound is the proposal creation whenever it's later than
																					// the last recorded trade — that's why the copy says "proposal creation".
																					minDate: utcDateToLocalDate(summary?.lastTradeDate),
																					maxDate,
																					children: (
																						<Text type="Body/M/Book" as="div" className="mb-4">
																							Choose a date between the proposal creation and yesterday.
																						</Text>
																					),
																					async onSubmitAsync(acceptAsDate) {
																						// The picker works in the user's local calendar; the API expects the
																						// equivalent date in the server timezone (Europe/Rome).
																						await proposalAction.dispatch("accept", {
																							acceptAsDate: localDateToServerDate(acceptAsDate),
																						});
																					},
																				});
																			}),
																			"data-qualifier": "PortfolioDetails/AcceptProposal/AcceptInThePast",
																		},
																	]
																: []),
														]}
														dataAttributes={{
															buttonDataAttributes: { "data-qualifier": "PortfolioDetails/AcceptProposal/Button" },
															dropdownTriggerDataAttributes: {
																"data-qualifier": "PortfolioDetails/AcceptProposal/DropdownTrigger",
															},
														}}
													>
														<Icon icon="Outline" />
														&nbsp;Accept
													</ButtonWithDropdown>
												)}
											</>
										)}
									</ReactQueryWrapperBase>

									<Button
										size="small"
										palette="info"
										onClick={() => {
											if (props.workspaceUid) {
												push("Portfolios/ManualEditPortfolioWithWorkspace", {
													workspaceUid: props.workspaceUid,
													uuid: summary.uuid,
												});
											} else {
												push("Portfolios/ManualEditPortfolio", { uuid: summary.uuid });
											}
										}}
										disabled={processing || !hasProposalWidgetsLoadedCorrectly || x.status === "CALCULATING"}
										data-qualifier="PortfolioDetails/EditProposal/Button"
									>
										<Icon icon="Edit" size={16} />
										&nbsp;Edit
									</Button>
									<Button
										size="small"
										palette="info"
										disabled={processing || !hasProposalWidgetsLoadedCorrectly || x.status === "CALCULATING"}
										onClickAsync={() => proposalAction.dispatch("reject")}
										data-qualifier="PortfolioDetails/RejectProposal/Button"
									>
										<Icon icon="Close" />
										&nbsp;Reject
									</Button>
								</AuthorizationGuard>
							</div>
						),
					)
					.when(
						(x) => x.status === "EXECUTING" && x.hasVisibleProposal && isEnhanceSelected,
						() => (
							<>
								<Button
									size="small"
									palette="infoOutline"
									disabled={processing}
									onClick={() => setIsEnhanceSelected(false)}
									style={{ borderColor: "#03a0dc" }}
									data-qualifier="PortfolioDetails/BackToCurrent/Button"
								>
									Back to current
								</Button>
								<AuthorizationGuard
									permissionChecker={aclByArea.portfolio.canCreateProposal}
									acl={summary.richAcl?.acl ?? []}
								>
									<ReactQueryWrapperBase query={customerQuery}>
										{(customer) => (
											<>
												{canSendToExecution(customer) && (
													<Button
														size="small"
														palette="primary"
														onClickAsync={() =>
															spawnYesNoDialog({
																header: "Are you sure you want to proceed?",
																children:
																	"The portfolio will return to the “Proposal Ready” status. This action won't communicate with any external system.",
																yesButton: "Done",
																noButton: "Cancel",
																onSubmitAsync: () => proposalAction.dispatch("execute"),
															})
														}
														data-qualifier="PortfolioDetails/StopeExecution/Button"
													>
														<Icon icon="Close" size={16} />
														&nbsp;Stop execution
													</Button>
												)}
											</>
										)}
									</ReactQueryWrapperBase>
								</AuthorizationGuard>
							</>
						),
					)
					.when(
						(x) => x.status === "REVIEW",
						() => (
							<Button
								size="small"
								palette="primary"
								onClick={() => {
									if (props.workspaceUid) {
										push("ReviewInstrumentsWithWorkspace", {
											uuid: summary.uuid,
											entity: ReviewEntity.INVESTMENT,
											workspaceUid: props.workspaceUid,
										});
									} else {
										push("ReviewInstruments", {
											uuid: summary.uuid,
											entity: ReviewEntity.INVESTMENT,
										});
									}
								}}
							>
								Review instruments
							</Button>
						),
					)
					.otherwise(() => <></>);
			})()}

			{!isEnhanceSelected &&
				(validateAction.canEditSettings(summary) || validateAction.canEditComposition(summary)) && (
					<DropdownMenu
						Trigger={EditPortfolioTrigger}
						extraTriggerProps={{
							"data-qualifier": "PortfolioDetails/EditDropdownMenu/Button",
							children: t("BUTTON.EDIT"),
						}}
						actions={[
							{
								disabled: !validateAction.canEditSettings(summary) || !hasProposalWidgetsLoadedCorrectly,
								icon: "Settings",
								label: "Edit portfolio settings",
								"data-qualifier": "PortfolioDetails/EditDropdownMenu/DropdownItem(settings)",
								onClick: () => {
									if (props.workspaceUid) {
										push("Portfolios/SettingsPortfolioWithWorkspace", {
											workspaceUid: props.workspaceUid,
											portfolioUid: summary.uuid,
										});
									} else {
										push("Portfolios/SettingsPortfolio", {
											portfolioUid: summary.uuid,
										});
									}
								},
							},

							{
								disabled: !validateAction.canEditComposition(summary),
								icon: "composition",
								label: "Edit composition",
								"data-qualifier": "PortfolioDetails/EditDropdownMenu/DropdownItem(composition)",
								onClick: () => {
									if (props.workspaceUid) {
										push("Portfolios/ManualEditPortfolioWithWorkspace", {
											workspaceUid: props.workspaceUid,
											uuid: summary.uuid,
										});
									} else {
										push("Portfolios/ManualEditPortfolio", {
											uuid: summary.uuid,
										});
									}
								},
							},
							{
								icon: "Content-Copy",
								disabled: !validateAction.canEditComposition(summary),
								label: "Copy composition",
								onClick: () => {
									if (props.workspaceUid) {
										push("Portfolios/ManualEditPortfolioWithWorkspace", {
											workspaceUid: props.workspaceUid,
											uuid: summary.uuid,
											copyComposition: "true",
										});
									} else {
										push("Portfolios/ManualEditPortfolio", {
											uuid: summary.uuid,
											copyComposition: "true",
										});
									}
								},
							},
							// Quantity-mode (MDOT-24700): the entry is offered to a customer entitled to the
							// PORTFOLIO_IN_QUANTITY service, on a drifting composition not already in quantities.
							...(canConvertToQuantities(user, summary)
								? ([
										{
											icon: "List-Numbers",
											disabled: !validateAction.canEditComposition(summary),
											label: "Convert to quantities",
											"data-qualifier": "PortfolioDetails/EditDropdownMenu/DropdownItem(convertToQuantities)",
											onClickAsync: async () => {
												await spawnConvertToQuantitiesDialog({
													portfolioUid: summary.uuid,
													portfolioName: summary.name,
													currency: summary.baseCurrency,
													onSubmitAsync: async ({ aum }) => {
														await runWithErrorReporting(
															async () => {
																try {
																	await quantityConversionRepository.createSharesProposal(summary.uuid, aum);
																} catch (error) {
																	throw new ToastableError(
																		"Something went wrong while converting the composition to quantities",
																		{ cause: error, icon: "Portfolio" },
																	);
																}
															},
															{
																area: "portfolio",
																attemptedOperation: {
																	message: `convert investment "${summary.uuid}" composition to quantities`,
																},
															},
														);
													},
												});
											},
										},
									] satisfies DropdownDropdownItem[])
								: []),
						]}
					/>
				)}

			<PageDownloadAndMoreActionsMenu
				area="portfolio"
				downloadActions={[
					...((["ACCEPTED", "READY", "PROPOSAL_READY"] satisfies InvestmentStatus[]).some((x) => x === summary.status)
						? [
								[
									"",
									[
										...filterMap(templateList ?? [], (template) =>
											!template.visible
												? []
												: [
														{
															icon: exportTemplateIcon(template),
															"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(portfolioTemplate)",
															onClickAsync: async () => {
																if (isStoryfolioV2Template(template)) {
																	const downloadOn = async (choice: TemplateChooserSubmitParams["choice"]) => {
																		await downloadStoryfolioV2Document(template, {
																			uuid: summary.uuid,
																			name: summary.name,
																			choice,
																		});
																		trackMixPanelEvent(
																			choice === "enhance"
																				? "portfolio.entityAction.downloadProposalReport"
																				: "portfolio.entityAction.downloadReport",
																			{
																				portfolio: { uuid: summary.uuid, name: summary.name! },
																				template: { uuid: exportTemplateId(template), name: template.templateName },
																			},
																		);
																	};
																	// A ready proposal is offered as the Report Builder offers it: the chooser, then one
																	// download per checked choice. Any other status downloads the current composition.
																	if (summary.status === "PROPOSAL_READY") {
																		spawnPortfolioTemplateChooser({
																			investments: [{ uuid: summary.uuid, name: summary.name, status: summary.status }],
																			// The gateway's generation request carries no date.
																			offerAsOf: false,
																			initialChoice: isEnhanceSelected ? "enhance" : undefined,
																			onSubmitAsync: async (userTemplatePreferences) => {
																				// TODO(MDOT-25287): with both stages checked, one failure leaves the other already downloaded and tracked,
																				// the dialog open with no message, and a retry downloading it again; settle both and toast what failed?
																				await parallelize(
																					userTemplatePreferences.map(
																						({ choice }) =>
																							() =>
																								downloadOn(choice),
																					),
																				);
																			},
																		});
																		return;
																	}
																	await downloadOn("current");
																	return;
																}

																if (summary.status === "PROPOSAL_READY") {
																	spawnPortfolioTemplateChooser({
																		investments: [
																			{
																				uuid: summary.uuid,
																				name: summary.name,
																				status: summary.status,
																			},
																		],
																		inceptionDate: summary.creationDate,
																		// The proposal view is never pinned to a date, so neither is its chooser.
																		offerAsOf: !isEnhanceSelected,
																		initialChoice: isEnhanceSelected ? "enhance" : undefined,
																		onSubmitAsync: async (userTemplatePreferences, { asOf }) => {
																			const reportConfs = generateReportBuilderTemplateUrl(
																				userTemplatePreferences,
																				template,
																				{ asOf },
																			);
																			await parallelize(
																				reportConfs.map((conf) => () => downloadSinglePdf(conf.name, conf)),
																			);
																			for (const { choice } of userTemplatePreferences) {
																				switch (choice) {
																					case "current":
																						trackMixPanelEvent("portfolio.entityAction.downloadReport", {
																							portfolio: {
																								uuid: summary.uuid,
																								name: summary.name!,
																							},
																							template: {
																								uuid: isDefaultReportTemplate(template) ? template.id : template.uuid!,
																								name: template.templateName!,
																							},
																						});
																						break;
																					case "enhance":
																						trackMixPanelEvent("portfolio.entityAction.downloadProposalReport", {
																							portfolio: {
																								uuid: summary.uuid,
																								name: summary.name!,
																							},
																							template: {
																								uuid: isDefaultReportTemplate(template) ? template.id : template.uuid!,
																								name: template.templateName!,
																							},
																						});
																						break;
																					default:
																						unreachable(choice);
																				}
																			}
																		},
																	});
																	return;
																}

																const [reportConf] = generateReportBuilderTemplateUrl(
																	[
																		{
																			name: summary.name,
																			uuid: summary.uuid,
																			choice: isEnhanceSelected ? "enhance" : "current",
																		},
																	],
																	template,
																	{ asOf: undefined },
																);

																await downloadSinglePdf(reportConf.name, reportConf);
																trackMixPanelEvent("portfolio.entityAction.downloadReport", {
																	portfolio: {
																		uuid: summary.uuid,
																		name: summary.name!,
																	},
																	template: {
																		uuid: template.uuid!,
																		name: template.templateName!,
																	},
																});
															},
															label: exportTemplateLabel(template),
														},
													],
										),
										{
											icon: "xls" as const,
											onClickAsync: async () => {
												const config = buildCompositionExportConfig("INVESTMENT", {
													investmentUuid: summary.uuid,
													snapshot: isEnhanceSelected ? "PROPOSAL" : "CURRENT",
													colStateKey: isEnhanceSelected
														? persistedColStateKeys["tableV3-investment-proposalComposition-read"]
														: persistedColStateKeys["tableV3-investment-composition-read"],
													compositionType: "STANDARD",
													proposalDate: summary.modificationDate,
													quantityMode: summary.quantityMode,
													currency: summary.baseCurrency,
													asOf: undefined,
												});
												await runWithErrorReporting(
													async () => {
														const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
														await downloadContentDisposition(file);

														trackMixPanelEvent(
															isEnhanceSelected
																? "portfolio.entityAction.downloadProposalComposition"
																: "portfolio.entityAction.downloadComposition",
															{
																portfolio: {
																	uuid: summary.uuid,
																	name: summary.name!,
																},
																format: "SPHERE_TEMPLATE_CONVERTER",
															},
														);
													},
													{
														area: "portfolio",
														attemptedOperation: {
															message: "export portfolio composition",
															payload: JSON.stringify(config),
														},
													},
												).catch(noop);
											},
											label: "Portfolio composition",
											"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(composition)",
										},
										...(isEnhanceSelected
											? [
													{
														icon: "xls" as const,
														onClickAsync: async () => {
															const response = await exportEnhancedApi.exportEnhancedComposition(
																summary.uuid,
																"TRADES_ONLY",
																{ responseType: "blob" },
															);

															await downloadContentDisposition(response);

															trackMixPanelEvent("portfolio.entityAction.downloadProposalTrades", {
																portfolio: {
																	uuid: summary.uuid,
																	name: summary.name!,
																},
															});
														},
														label: t("COMPOSITION.DOWNLOAD_TRADES_TITLE"),
														"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(trades)",
													},
												]
											: []),
										...(hasAccess(user, { requiredService: "EXPORT" })
											? (user.exportFormats ?? []).map((format) => ({
													icon: "xls" as const,
													onClickAsync: async () => {
														if (format === InvestmentExportConverterType.EasimTemplateConverter) {
															const composition = await axiosExtract(
																integrationsApi.exportInvestment(summary.uuid, isEnhanceSelected),
															);
															await axiosExtract(integrationsApi.convertTo(format, [composition], true));
															if (isEnhanceSelected) {
																trackMixPanelEvent("portfolio.entityAction.downloadProposalComposition", {
																	portfolio: {
																		uuid: summary.uuid,
																		name: summary.name!,
																	},
																	format,
																});
															} else {
																trackMixPanelEvent("portfolio.entityAction.downloadComposition", {
																	portfolio: {
																		uuid: summary.uuid,
																		name: summary.name!,
																	},
																	format,
																});
															}
															platformToast({
																children: "Sphere has taken over your request",
																severity: "info",
																icon: "Dowload",
															});
															return;
														}

														const download = async () => {
															await downloadInvestmentConversion(format, summary.uuid, isEnhanceSelected);

															if (isEnhanceSelected) {
																trackMixPanelEvent("portfolio.entityAction.downloadProposalComposition", {
																	portfolio: {
																		uuid: summary.uuid,
																		name: summary.name!,
																	},
																	format,
																});
															} else {
																trackMixPanelEvent("portfolio.entityAction.downloadComposition", {
																	portfolio: {
																		uuid: summary.uuid,
																		name: summary.name!,
																	},
																	format,
																});
															}
														};

														if (format === InvestmentExportConverterType.SmartxConverter) {
															// SmartX's refusals have to reach the user; the other formats show the menu's error state alone.
															await runWithErrorReporting(download, {
																area: "portfolio",
																attemptedOperation: {
																	message: "download portfolio composition",
																	payload: JSON.stringify({
																		uuid: summary.uuid,
																		format,
																		enhancement: isEnhanceSelected,
																	}),
																},
															});
															return;
														}

														await download();
													},
													label: `${t(`EXPORT.${format}`)} template`,
													"data-qualifier": `PortfolioDetails/DropdownMenu/DropdownItem(portfolioTemplate,${format})`,
												}))
											: []),
										...(hasAccess(user, {
											requiredService: "INVESTMENTS_REPORT_TEMPLATE_EDITOR",
										})
											? [
													{
														icon: "Settings" as const,
														onClick: () => {
															push("PortfolioStudioSettings", {
																tab: PortfolioStudioSettingTabEnum.ReportCustomisation,
															});
														},
														label: "Report builder",
														"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(reportCustomisation)",
													},
												]
											: []),
									],
								] satisfies [title: string, actions: Array<DropdownDropdownItem>],
							]
						: []),
					...(hasAccess(user, { requiredRole: "ROOT" })
						? [
								[
									"ROOT FEATURES",
									[
										{
											icon: "Expand" as const satisfies IconName,
											"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(PreviewReport)",
											dropdownItems: filterMap(previewTemplateList ?? [], (template) =>
												!template.visible
													? []
													: [
															{
																icon: "pdf" as const,
																label: template.templateName ?? "",
																onClick: () => {
																	window.open(
																		typedUrlForRoute("Report", {
																			templateId: isDefaultReportTemplate(template) ? template.id : template.uuid!,
																			objectId: summary.uuid,
																			variant: (isEnhanceSelected
																				? "proposal"
																				: "current") satisfies ReportTemplateVariant,
																		}),
																		"_blank",
																	);
																},
															},
														],
											),
											disabled: summary.status === "ERROR" || summary.status === undefined,
											label: "Preview report",
										},
									],
								] satisfies [title: string, actions: Array<DropdownDropdownItem>],
							]
						: []),
				]}
				moreActions={[
					...(isEnhanceSelected
						? []
						: [
								...(aclByArea.portfolio.canDelete(user.id, summary.richAcl?.acl ?? [])
									? [
											{
												icon: "Delete" as const,
												"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Delete)",
												onClick: unpromisify(entityManagementActions.deleteAsync ?? noop),
												disabled: !entityManagementActions.deleteAsync,
												label: "Delete",
											},
										]
									: []),
								{
									icon: "Content-Copy" as const,
									"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Duplicate)",
									onClick: unpromisify(entityManagementActions.duplicateAsync ?? noop),
									disabled: !entityManagementActions.duplicateAsync,
									label: "Duplicate",
								},
								...(entityManagementActions.renameAsync
									? [
											{
												"data-qualifier": "PortfolioDetails/DropdownMenu/DropdownItem(Rename)",
												icon: "Edit" as const,
												onClick: unpromisify(entityManagementActions.renameAsync),
												label: "Rename",
											},
										]
									: []),
							]),
				]}
			/>
		</div>
	);
}

/** @internal — exported for tests */
export function validatePortfolioAction() {
	function canEditSettings(summary: InvestmentSummaryWithRequiredUuid) {
		const user = readAtom(user$);
		return (
			(summary.status === "READY" || summary.status === "PROPOSAL_READY" || summary.status === "ACCEPTED") &&
			aclByArea.portfolio.canEditSettings(user.id, [
				{
					userId: user.id,
					permissions: summary.richAcl?.currentUserPermissions,
				},
			])
		);
	}

	function canEditComposition(summary: InvestmentSummaryWithRequiredUuid) {
		const user = readAtom(user$);
		return (
			(summary.status === "READY" || summary.status === "ACCEPTED") &&
			aclByArea.portfolio.canEditComposition(user.id, [
				{
					userId: user.id,
					permissions: summary.richAcl?.currentUserPermissions,
				},
			])
		);
	}

	return { canEditComposition, canEditSettings };
}

/**
 * @param leaveProposalView drops the `proposal=true` view. Accept and reject call it *before* their
 * request goes out — see the comment on the `accept` branch.
 */
/** @internal — exported for tests */
export function handleProposalAction(
	uuid: string,
	name: string,
	{ isExecuting, leaveProposalView }: { isExecuting: boolean; leaveProposalView(): void },
) {
	const processing$ = atom(false);
	async function dispatch(accept: "accept" | "reject" | "execute", opts?: { acceptAsDate?: string }) {
		await runWithErrorReporting(
			async () => {
				try {
					writeAtom(processing$, true);
					if (!uuid) {
						throw new Error("portfolio not found");
					}

					switch (accept) {
						case "accept": {
							trackMixPanelEvent("portfolio.entityActions.acceptProposal", {
								portfolio: { uuid, name },
							});
							// Accepting deletes the proposal, and the action closes by invalidating the entity — which
							// refetches every *mounted* query holding this uuid. In the proposal view that is the whole
							// widget wall reading the enhancement reports, so the invalidation would fire a burst of
							// requests for the proposal this call just destroyed.
							//
							// So the view leaves proposal mode before the request goes out, not after it resolves: a
							// full round trip later, when the invalidation lands, nothing that asks for the proposal
							// is mounted any more. The alternative — flipping afterwards — cannot work, because the
							// invalidation is already over by the time the caller regains control.
							leaveProposalView();
							await portfolioRepository.acceptEnhancement(uuid, opts?.acceptAsDate);
							break;
						}
						case "reject": {
							trackMixPanelEvent("portfolio.entityActions.rejectProposal", {
								portfolio: { uuid, name },
							});
							// Same reason as `accept`: the proposal is gone once this resolves.
							leaveProposalView();
							await portfolioRepository.rejectEnhancement(uuid);
							break;
						}
						case "execute": {
							if (isExecuting) {
								trackMixPanelEvent("portfolio.entityActions.cancelExecution", {
									portfolio: { uuid, name },
								});
							} else {
								trackMixPanelEvent("portfolio.entityActions.execute", {
									portfolio: { uuid, name },
								});
							}
							await portfolioRepository.toggleExecution(uuid);
							break;
						}
					}
				} catch (error) {
					throw new ToastableError(
						i18next.t("SOMETHING_WENT_WRONG", {
							cause: error,
							icon: "Portfolio",
						}),
					);
				} finally {
					writeAtom(processing$, false);
				}
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: `${accept} enhancement on investment "${uuid}"`,
				},
			},
		);
	}

	return { dispatch, processing$ };
}

type ComparativeBenchmarkDialogProps = SpawnComparativeBenchmarkDialogProps & MinimumDialogProps;
function ComparativeBenchmarkDialog({
	uuid,
	show,
	onSubmitAsync,
	onClose,
	onAnimationStateChange,
	preselectedComparativeBenchmarksIds,
}: ComparativeBenchmarkDialogProps) {
	const selectableComparativeBenchmarksRaw = referenceRepository.comparativeBenchmarkList.useQuery(uuid);
	const selectableComparativeBenchmarks = useMappedQuery(
		({ availableBenchmarks }) => {
			return availableBenchmarks ?? [];
		},
		[],
		selectableComparativeBenchmarksRaw,
	);

	const handleSubmitRef = useUnsafeUpdatedRef<{
		dispatch: () => Promise<void>;
	} | null>(null);
	async function handleSubmit(ctx: MultiSelectCtx<string>, availableBenchmarks: InvestmentBenchmarkDTO[]) {
		await onSubmitAsync(availableBenchmarks.filter((x) => ctx.selection.has(x?.benchmarkIdentifier ?? "")));
	}

	return (
		<Dialog
			header="Select benchmarks"
			show={show}
			onClose={onClose}
			onAnimationStateChange={onAnimationStateChange}
			size="large"
			onSubmitAsync={() => handleSubmitRef.current?.dispatch()}
			footer={
				<DialogFooter
					neutralAction={
						<Button palette="tertiary" onClick={onClose} data-qualifier="PortfolioDetails/ComparativeBenchmark/Cancel">
							Close
						</Button>
					}
					primaryAction={
						<SubmitButton data-qualifier="PortfolioDetails/ComparativeBenchmark/Confirm">Confirm</SubmitButton>
					}
				/>
			}
			className="w-[960px]!"
		>
			<ReactQueryWrapperBase query={selectableComparativeBenchmarks}>
				{(availableBenchmarks) => (
					<ComparativeBenchmarkInner
						availableBenchmarks={availableBenchmarks}
						preselectedComparativeBenchmarksIds={preselectedComparativeBenchmarksIds}
						handleSubmitRef={handleSubmitRef}
						handleSubmit={handleSubmit}
					/>
				)}
			</ReactQueryWrapperBase>
		</Dialog>
	);
}

function ComparativeBenchmarkInner({
	availableBenchmarks,
	preselectedComparativeBenchmarksIds,
	handleSubmitRef,
	handleSubmit,
}: {
	availableBenchmarks: InvestmentBenchmarkDTO[];
	preselectedComparativeBenchmarksIds: string[];
	handleSubmitRef: RefObject<{
		dispatch: () => Promise<void>;
	} | null>;
	handleSubmit(ctx: MultiSelectCtx<string>, availableBenchmarks: InvestmentBenchmarkDTO[]): Promise<void>;
}) {
	const { t } = useTranslation();
	const selectableRows = useMemo(
		() => availableBenchmarks.filter((x) => x.available && x.benchmarkIdentifier),
		[availableBenchmarks],
	);
	const selectableRowIds = useMemo(() => selectableRows.map((x) => x.benchmarkIdentifier!), [selectableRows]);

	const { filtered, query, setQuery } = useSearchable({
		collection: availableBenchmarks,
		extractFn: (row) =>
			Object.values({
				...row,
				benchmarkType: benchmarkTypeToLabel[row.benchmarkType!],
			}).join(" "),
	});

	const {
		column: checkboxColumn,
		rowClassName,
		toggle,
		multiSelectCtx,
	} = useSelectableTableColumn({
		alwaysSelected: preselectedComparativeBenchmarksIds,
		rows: availableBenchmarks,
		filteredRows: filtered,
		selectableRowIds,
		selectBy: (r) => r.benchmarkIdentifier!,
	});

	useImperativeHandle(handleSubmitRef, () => ({
		async dispatch() {
			await handleSubmit(multiSelectCtx, availableBenchmarks);
		},
	}));

	return (
		<>
			<div className="pb-4">
				<TextInput
					placeholder="Search..."
					value={query}
					onChangeText={setQuery}
					innerRef={(e) => e?.setAttribute("data-qualifier", "PortfolioDetails/ComparativeBenchmark/Search")}
				/>
			</div>
			<AutoSortTable
				rows={filtered}
				rowClassName={(row, rowIndex) => ({
					...toClassListRecord(rowClassName(row, rowIndex)),
					"opacity-60": !selectableRowIds.includes(row.benchmarkIdentifier!),
				})}
				onRowClick={(r) => toggle(r.benchmarkIdentifier ?? "")}
				columns={[
					checkboxColumn,
					{
						name: "name",
						header: t("TABLE.HEADERS.NAME"),
						content: (benchmark, cellProps) => (
							<TableDataCell
								data-qualifier={`PortfolioDetails/ComparativeBenchmark/Item(${benchmark.benchmarkName})`}
								title={benchmark.benchmarkName}
								{...cellProps}
							>
								{createPrimaryBenchmarkLabel(benchmark)}
							</TableDataCell>
						),
						sortFn: builtInSortFnFor("benchmarkName"),
						width: "65%",
					},
					{
						name: "type",
						header: t("TABLE.HEADERS.TYPE"),
						content: (row) => benchmarkTypeCellLabel(row),
						width: "25%",
						sortFn: builtInSortFnFor("benchmarkType"),
					},
				]}
				className="max-h-[410px]"
				noDataText={
					<div className="flex items-center justify-center h-20">
						<p>No Data</p>
					</div>
				}
			/>
		</>
	);
}

type SpawnComparativeBenchmarkDialogProps = {
	uuid: string;
	preselectedComparativeBenchmarksIds: string[];
	onSubmitAsync(newComparativeBenchmarkSelection: InvestmentBenchmarkDTO[]): Promise<void>;
};

export function spawnComparativeBenchmarkDialog(props: SpawnComparativeBenchmarkDialogProps): SpawnResult<void> {
	return spawn<void>(
		adaptAnimatedNodeProvider(({ show, resolve, onHidden }) => (
			<ComparativeBenchmarkDialog
				{...props}
				show={show}
				onAnimationStateChange={(state) => state === "hidden" && onHidden()}
				onClose={() => resolve()}
				onSubmitAsync={async (payload) => {
					await props.onSubmitAsync(payload);
					resolve();
				}}
			/>
		)),
	);
}

function ComparativeBenchmark({
	value,
	uuid,
	onChange,
	canAddBenchmark,
	canSelectBenchmark,
}: {
	uuid: string;
	value: string | null;
	onChange?(v: string): void;
	canAddBenchmark?: boolean;
	/** The picker is read-only when false: an as-of snapshot is pinned to the benchmark it was computed with. */
	canSelectBenchmark?: boolean;
}) {
	const [selectedComparativeBenchmark, setSelectedComparativeBenchmark] = useDrivenState(value, {
		onSet: onChange,
	});

	const { t } = useTranslation();

	const addNewComparativeBenchmarks = useCallback(
		async (
			portfolioUuid: string,
			selection: {
				newSecondaryBenchmarks: InvestmentBenchmarkDTO[];
				primaryBenchmark: InvestmentBenchmarkDTO;
			},
		) => {
			await runWithErrorReporting(
				async () => {
					try {
						await referenceRepository.setComparativeBenchmark(portfolioUuid, {
							primaryBenchmark: selection.primaryBenchmark,
							secondaryBenchmarks: selection.newSecondaryBenchmarks,
						});

						referenceRepository.invalidate(referenceRepository.comparativeBenchmark.partialQueryKey);
						platformToast({
							children: "Successfully updated the benchmarks",
							severity: "success",
							icon: "Portfolio",
						});
					} catch (error) {
						throw new ToastableError(t("SOMETHING_WENT_WRONG"), {
							cause: error,
						});
					}
				},
				{
					area: "portfolio",
					attemptedOperation: {
						message: `set benchmark on investment "${portfolioUuid}"`,
						payload: JSON.stringify({ uuid: portfolioUuid, selection }),
					},
				},
			);
		},
		[t],
	);

	const comparativeBenchmarksLinkedToInvestment = referenceRepository.comparativeBenchmark.useQuery(uuid);
	const comparativeBenchmarksOptionsQuery = useMappedQuery(
		({ secondaryBenchmarks, primaryBenchmark }) => {
			let options: Array<Option<string>> = [];
			if (primaryBenchmark?.benchmarkIdentifier && primaryBenchmark?.benchmarkName) {
				options.push({
					value: primaryBenchmark.benchmarkIdentifier,
					label: primaryBenchmark.benchmarkName,
					disabled: !primaryBenchmark?.available,
				});
			}

			options = options.concat(
				alwaysArray(secondaryBenchmarks)
					.filter((x) => x?.benchmarkIdentifier !== undefined)
					.map((x) => ({
						label: x!.benchmarkName!,
						value: x!.benchmarkIdentifier!,
						disabled: !x?.available,
					})),
			);

			return {
				options,
				secondaryBenchmarks,
				primaryBenchmark,
			};
		},
		[],
		comparativeBenchmarksLinkedToInvestment,
	);

	useQueryReactor(comparativeBenchmarksLinkedToInvestment, {
		onSuccess(response) {
			if (response.primaryBenchmark?.benchmarkIdentifier) {
				onChange?.(response.primaryBenchmark.benchmarkIdentifier);
			}
		},
	});

	return (
		<FormField label="Benchmark" className="ml-auto flex-col lg:gap-1 lg:flex-row lg:items-center">
			{({ id }) => (
				<div className="flex items-center space-x-2">
					<ReactQueryWrapperBase query={comparativeBenchmarksOptionsQuery}>
						{({ options, primaryBenchmark }) => (
							<>
								<Select
									className="min-w-16 max-w-32 lg:max-w-56"
									size="x-small"
									id={id}
									options={options}
									value={selectedComparativeBenchmark}
									onChange={setSelectedComparativeBenchmark}
									disabled={!(canSelectBenchmark ?? true)}
									triggerDataAttrs={{
										"data-qualifier": "PortfolioDetails/PageHeader/Action/SelectComparativeBenchark",
									}}
								/>

								<Button
									size="x-small"
									palette="neutralOutline"
									disabled={!(canAddBenchmark ?? true)}
									onClick={() =>
										spawnComparativeBenchmarkDialog({
											uuid,
											preselectedComparativeBenchmarksIds: options.map((x) => x.value),
											async onSubmitAsync(newSecondaryBenchmarks) {
												await addNewComparativeBenchmarks(uuid, {
													primaryBenchmark: primaryBenchmark!,
													newSecondaryBenchmarks: newSecondaryBenchmarks.filter(
														(x) => x.benchmarkIdentifier !== primaryBenchmark?.benchmarkIdentifier,
													),
												});
											},
										})
									}
									data-qualifier="PortfolioDetails/PageHeader/Action/AddComparativeBenchmark"
								>
									<Icon icon="Outline1" />
								</Button>
							</>
						)}
					</ReactQueryWrapperBase>
				</div>
			)}
		</FormField>
	);
}

const benchmarkTypeToLabel = {
	CURRENT_PORTFOLIO: "Portfolio benchmark",
	CUSTOM_BENCHMARK: "Benchmark",
	REFERENCE_INVESTMENT: "Reference",
	STANDARD_BENCHMARK: "Standard benchmark",
} satisfies Record<InvestmentBenchmarkDTOBenchmarkTypeEnum, string>;

/**
 * @internal — exported for tests
 *
 * Label shown in the comparative-benchmark picker's "type" column. A reference investment that is
 * flagged as a benchmark, and any custom benchmark, both read simply as "Benchmark"; everything else
 * maps through {@link benchmarkTypeToLabel}, and a row with no type at all shows nothing.
 *
 * (The picker's table rows are virtualized and never mount under jsdom, so this rule is only
 * reachable directly — hence the extraction.)
 */
export function benchmarkTypeCellLabel(row: InvestmentBenchmarkDTO): string {
	if (
		(row.flaggedAsBenchmark && row.benchmarkType === "REFERENCE_INVESTMENT") ||
		row.benchmarkType === "CUSTOM_BENCHMARK"
	) {
		return "Benchmark";
	}
	return row.benchmarkType ? benchmarkTypeToLabel[row.benchmarkType] : "";
}

const severityByStatus: Record<InvestmentStatus, PageHeaderProps["severity"]> = {
	ACCEPTED: "success",
	READY: "success",
	PROPOSAL_READY: "info",
	// Temporarily (MDOT-18271) mask Error status as Calculating until BE fixes their process
	// ERROR: "error",
	ERROR: "calculating",
	CALCULATING: "calculating",
	REVIEW: undefined,
	RETRIEVING_DATA: "calculating",
	DRAFT: "info",
	EXECUTING: "calculating",
};

const PortfolioDetails = (): React.JSX.Element => {
	const { workspaceUid, portfolioUid } = useParams<PageProps>();
	const searchParams = useSearchParams();
	const rawAsOf = searchParams.asOf;
	// `?asOf` and `?proposal` are mutually exclusive: an as-of view shows only official data (R11).
	const enhanced = searchParams.proposal === "true" && rawAsOf == null;
	const history = useHistory();
	const { t } = useTranslation();
	const { push } = useTypedNavigation();

	const setEnhanced = useCallback(
		(newIsEnhanceSelected: boolean) => {
			const queryParams = new URLSearchParams(history.location.search);
			if (newIsEnhanceSelected === enhanced) {
				return;
			}
			if (newIsEnhanceSelected) {
				queryParams.set("proposal", "true");
			} else {
				queryParams.delete("proposal");
			}
			history.replace({ search: queryParams.toString() });
		},
		[history, enhanced],
	);

	const setAsOf = useCallback(
		(newAsOf: string | null) => {
			const queryParams = new URLSearchParams(history.location.search);
			const currentAsOf = queryParams.get(AS_OF_SEARCH_PARAM);
			if (newAsOf === currentAsOf) {
				return;
			}
			if (newAsOf != null) {
				queryParams.set(AS_OF_SEARCH_PARAM, newAsOf);
				queryParams.delete("proposal");
			} else {
				queryParams.delete(AS_OF_SEARCH_PARAM);
			}
			// Entering/leaving the as-of view pushes (back exits in one press);
			// changing the date while already in it replaces (no stack build-up).
			if (currentAsOf != null && newAsOf != null) {
				history.replace({ search: queryParams.toString() });
			} else {
				history.push({ search: queryParams.toString() });
			}
		},
		[history],
	);

	const leaveProposalView = useCallback(() => setEnhanced(false), [setEnhanced]);
	const {
		query: portfolioSummary,
		hasVisibleProposal,
		refetch: refetchSummary,
	} = useResolvedSummary(portfolioUid, { enhanced, onProposalUnavailable: leaveProposalView });

	const asOf = useMemo(
		() => parseAsOfParam(rawAsOf, { inceptionDate: portfolioSummary.data?.creationDate }),
		[rawAsOf, portfolioSummary.data?.creationDate],
	);

	useEffect(() => {
		// Invalid `?asOf` values (unparsable, weekend, before inception, not in the past)
		// silently fall back to the live view and are dropped from the URL (R8).
		if (rawAsOf != null && asOf == null) {
			const queryParams = new URLSearchParams(history.location.search);
			queryParams.delete(AS_OF_SEARCH_PARAM);
			history.replace({ search: queryParams.toString() });
		}
	}, [rawAsOf, asOf, history]);

	const portfolioStudio = usePortfolioStudioRedirect();
	useQueryReactor(portfolioSummary, {
		onError(error) {
			const { data } = (error as any)?.response ?? {};
			if (data?.message === "Accesso negato" || data?.code === 404) {
				spawnAccessDialog({
					onClick: (onClose) => {
						portfolioStudio.navigate();
						onClose();
					},
				});
				return;
			}

			platformToast({
				children: t("SOMETHING_WENT_WRONG"),
				severity: "error",
				icon: "Portfolio",
			});
			portfolioStudio.navigate();
		},
		onSuccess(data) {
			if (data.status === "REVIEW") {
				const entity = data?.reference ? ReviewEntity.TARGET_INVESTMENT : ReviewEntity.INVESTMENT;
				navigateToReviewIfAnythingToReviewSync(portfolioUid, entity, () => {
					if (workspaceUid) {
						push("ReviewInstrumentsWithWorkspace", {
							uuid: portfolioUid,
							entity,
							workspaceUid,
						});
					} else {
						push("ReviewInstruments", { uuid: portfolioUid, entity });
					}
				});
				// Still short-circuits: the branches below are for other statuses, and the last one rewrites
				// the URL's date parameter, which a portfolio sitting in REVIEW should not be doing.
				return;
			}

			if (data.status === "DRAFT" && data.action === "UPLOAD") {
				push("Portfolios/ManualPortfolioCreation", { uuid: portfolioUid });
				return;
			}

			if (data.status === "DRAFT" && data.action === "CREATION") {
				push("Portfolios/Draft", { portfolioUid });
				return;
			}
		},
	});

	useGroupEventBus("investment-update", () => refetchSummary().catch(noop), {
		filter: objMatchFn({ uuid: portfolioUid }),
		timeout: seconds(2),
	});

	useEventBus("investment-import-update", {
		filter: objMatchFn({ uuid: portfolioUid }),
		listener(e) {
			if (
				(e as { status?: string }).status === "CALCULATING" &&
				portfolioSummary.data?.status === "EXECUTING" &&
				enhanced
			) {
				setEnhanced(false);
				return;
			}
			refetchSummary().catch(noop);
		},
	});

	useGroupEventBus("investment-report-update", () => refetchSummary().catch(noop), {
		filter: objMatchFn({ uuid: portfolioUid }),
		timeout: seconds(2),
	});

	useEventBus("shared-entity", {
		filter: objMatchFn({ sharedEntityUuid: portfolioUid }),
		listener: () => {
			refetchSummary().catch(noop);
		},
	});

	return (
		<ReactQueryWrapperBase query={portfolioSummary}>
			{(summary) => (
				<PortfolioDetailsInner
					isEnhanceSelected={enhanced}
					setIsEnhancedSelected={setEnhanced}
					asOf={asOf ?? undefined}
					setAsOf={setAsOf}
					summary={{ ...summary, uuid: portfolioUid }}
					hasVisibleProposal={hasVisibleProposal}
					workspaceUid={workspaceUid}
				/>
			)}
		</ReactQueryWrapperBase>
	);
};

const PortfolioDetailsInner = ({
	isEnhanceSelected,
	setIsEnhancedSelected,
	asOf,
	setAsOf,
	summary,
	hasVisibleProposal,
	workspaceUid,
}: {
	summary: InvestmentSummaryWithRequiredUuid;
	isEnhanceSelected: boolean;
	setIsEnhancedSelected(enhanced: boolean): void;
	/** From the portfolio's own summary — `summary` may be the proposal's, which outlives the proposal in cache. */
	hasVisibleProposal: boolean;
	/** Validated `?asOf` date (`YYYY-MM-DD`); set = the page shows a past snapshot (history mode). */
	asOf?: string;
	setAsOf(asOf: string | null): void;
	workspaceUid?: string;
}): React.JSX.Element => {
	const { reference } = summary ?? {};
	const portfolioUid = summary.uuid;
	const isHistoryMode = asOf != null;

	const history = useHistory();
	const user = useUserValue();
	const crumbs = useCrumbs();
	const { push } = useTypedNavigation();

	const [selectedComparativeBenchmark, setSelectedComparativeBenchmark] = useState<string | null>(null);

	// Asks the backend to make the pinned date's snapshot available and reports whether the widgets may
	// read it yet (MDOT-24860). No-op in the live view.
	const asOfSnapshot = useAsOfSnapshot(portfolioUid, asOf);

	useFunctionalAreas(
		() => ({
			areas: "portfolio",
			data: { uuid: portfolioUid, name: summary.name, proposal: isEnhanceSelected },
		}),
		[portfolioUid, summary.name, isEnhanceSelected],
	);

	// portfolio alert api call
	const gridName = useMemo(() => (isEnhanceSelected ? ENH_PAGE_NAME : PAGE_NAME), [isEnhanceSelected]);
	const gridHandleRef = useRef<WidgetGridHandles | null>(null);

	const requestEnhance = useCallback(() => {
		const intentId = generateIntentId();
		trackMixPanelEvent("portfolio.entityActions.createProposalIntent", {
			intentId,
			portfolio: {
				uuid: summary.uuid ?? "",
				name: summary.name ?? "",
			},
		});
		if (workspaceUid) {
			push("Portfolios/EditPortfolioWithWorkspace", {
				workspaceUid,
				portfolioUid,
				intentId,
			});
		} else {
			push("Portfolios/EditPortfolio", { portfolioUid, intentId });
		}
	}, [summary.uuid, summary.name, push, portfolioUid, workspaceUid]);

	const onChangePortfolioUid = useCallback(
		(uuid: string) => {
			setIsEnhancedSelected(false);
			if (reference) {
				push("ReferenceDetails", { uuid: uuid });
			} else if (workspaceUid) {
				push("PortfolioDetailsWithWorkspace", {
					workspaceUid,
					portfolioUid: uuid,
				});
			} else {
				push("PortfolioDetails", { portfolioUid: uuid });
			}
		},
		[push, reference, setIsEnhancedSelected, workspaceUid],
	);

	// context
	// those callback context make widgets depending to ptf details
	const portfolioContextData = useMemo<ContextContent<typeof PortfolioContext>>(
		() => ({
			enhanced: isEnhanceSelected,
			asOf,
			asOfSnapshot,
			portfolio: summary,
			selectedBenchmark: selectedComparativeBenchmark,
			currentTab: 0,
			requestEnhance: summary.status === "READY" || summary.status === "ACCEPTED" ? requestEnhance : undefined,
			onChangePortfolioUid,
			workspaceUid,
		}),
		[
			isEnhanceSelected,
			asOf,
			asOfSnapshot,
			summary,
			selectedComparativeBenchmark,
			requestEnhance,
			onChangePortfolioUid,
			workspaceUid,
		],
	);

	const area = useMemo(() => {
		const areaName = isEnhanceSelected ? ("settings-enhanced" as const) : ("settings-current" as const);

		return {
			name: areaName,
			portfolioUid,
			editable: false /* readonly, edit settings will navigate to a dedicated page */,
			edit: false,
		};
	}, [isEnhanceSelected, portfolioUid]);

	const availableTabs = getAvailableTabs({ gridName, isEnhanceSelected, isHistoryMode, asOf, user, gridHandleRef });

	const tabs = customObjectEntriesFn(availableTabs).filter(([_, v]) => v.hasAccess);
	const tabSearchParams = useSearchParams().tab ?? tabs[0][0];
	// `?tab` may name a tab this view does not offer (e.g. Portfolio Settings once the page is pinned to
	// a past date): fall back to the first tab rather than rendering none. The param is left untouched so
	// returning to today lands back on the tab the user was on.
	const currentTab = Math.max(
		0,
		tabs.findIndex(([tabName]) => tabName === tabSearchParams),
	);
	const setCurrentTab = useCallback(
		(tabIndex: number) => {
			if (currentTab === tabIndex) {
				return;
			}
			const newSearchParams = new URLSearchParams(history.location.search);
			if (tabs[tabIndex][0] !== "PORTFOLIO_STUDIO_SETTINGS") {
				newSearchParams.delete("edit");
			}
			newSearchParams.set("tab", String(tabs[tabIndex][0]));
			history.replace({
				search: newSearchParams.toString(),
				hash: history.location.hash,
			});
		},
		[currentTab, history, tabs],
	);

	const portfolioStudio = usePortfolioStudioRedirect();
	const entityManagementActions = usePortfolioEntityManagementActions(summary, {
		onDuplicate: () => portfolioStudio.navigate(),
		onDelete: () => portfolioStudio.navigate(),
	});
	const workspacePath = crumbs.useWorkspacePath(workspaceUid);

	const investmentEnhancementReportApi = useApiGen(InvestmentEnhancementReportsControllerApiFactory);
	const monitoringForEnhancedApi = useApiGen(InvestmentEnhancementReportsControllerApiFactory);
	const [canRetry, setCanRetry] = useState(true);
	const [retryCount, _setRetryCount] = useState(0);
	const hasProposalWidgetsLoadedCorrectlyQuery = useQueryNoRefetch(["queryEnhancedWidgetDataStatus"], {
		async queryFn() {
			setRetryCount((retry) => retry + 1);
			// Same gate as the proposal summary: every call below reads the enhancement reports, so a
			// poll tick must not go out for a proposal that has just been accepted, rejected or executed.
			if (summary.status !== "PROPOSAL_READY" || !hasVisibleProposal) {
				return true;
			}

			if (!benchmarkId) {
				return false;
			}

			const exposure = await axiosExtract(
				investmentEnhancementReportApi.getTwoLevelsInvestmentExposure1(
					summary.uuid,
					benchmarkId,
					"MACRO_ASSET_CLASS_VS_MICRO_ASSET_CLASS",
				),
			);

			const isExposureAvailable = exposure.enhancementComposition != null && exposure.investmentComposition != null;

			const exAnteContributionVolatilityResponse = await axiosExtract(
				investmentEnhancementReportApi.getCompositionVolatilityContribution1(summary.uuid, "ONE_MONTH"),
			);

			const isExanteContributionAvailable =
				exAnteContributionVolatilityResponse.current != null || exAnteContributionVolatilityResponse.proposal != null;

			const exAnteMetrics = await axiosExtract(
				investmentEnhancementReportApi.getPortfolioExAnteMetrics1(summary.uuid, benchmarkId),
			);

			const isExAnteMetricsAvailable = exAnteMetrics.portfolioExAnteMetrics != null;

			const enhancementsFactors = await axiosExtract(
				investmentEnhancementReportApi.getInvestmentFactors1(summary.uuid, benchmarkId),
			);

			const isEnhancementsFactorsAvailable =
				enhancementsFactors.current != null ||
				enhancementsFactors.benchmark != null ||
				enhancementsFactors.proposal != null;

			const monitoringMetrics = await axiosExtract(monitoringForEnhancedApi.getMonitoringMetrics1(summary.uuid));
			const isMonitoringAvailable = monitoringMetrics.monitoringMetrics != null;

			return (
				isExposureAvailable &&
				isExanteContributionAvailable &&
				isExAnteMetricsAvailable &&
				isEnhancementsFactorsAvailable &&
				isMonitoringAvailable
			);
		},
		keepPreviousData: true,
		retry: canRetry,
		refetchInterval: canRetry ? seconds(10) : false,
	});

	const retryCountRef = useRef(retryCount);
	function setRetryCount(newCount: Updater<number> | number) {
		_setRetryCount((latestCount) => {
			if (typeof newCount === "function") {
				const resultCount = newCount(latestCount);
				retryCountRef.current = resultCount;
				return resultCount;
			}

			retryCountRef.current = newCount;
			return newCount;
		});
	}

	const benchmarkId = useMemo(
		() => selectedComparativeBenchmark ?? summary.primaryBenchmarkIdentifier,
		[selectedComparativeBenchmark, summary.primaryBenchmarkIdentifier],
	);

	const retryLimitRef = useRef(30);

	useQueryReactor(hasProposalWidgetsLoadedCorrectlyQuery, {
		onSuccess(areAllProposalReportsAvailable) {
			if (areAllProposalReportsAvailable || retryCountRef.current >= retryLimitRef.current) {
				setCanRetry(false);
			}
		},
	});

	return (
		<PortfolioContext.Provider value={portfolioContextData ?? {}}>
			<EditPortfolioV4ContextProvider area={area}>
				{() => {
					return (
						<>
							<PageHeader
								severity={isHistoryMode ? undefined : summary?.status ? severityByStatus[summary?.status] : undefined}
								// Pinned to a past date the header carries no permission badge and no live status —
								// hidden, not disabled (R13). The date itself moves to the sticky bar below.
								titleAction={
									isHistoryMode ? (
										<PortfolioHistoryTitleActions summary={summary} asOf={asOf} asOfSnapshot={asOfSnapshot} />
									) : (
										<PortfolioTitleActions
											summary={summary}
											isEnhanceSelected={isEnhanceSelected}
											setIsEnhanceSelected={setIsEnhancedSelected}
											entityManagementActions={entityManagementActions}
											hasProposalWidgetsLoadedCorrectlyQuery={hasProposalWidgetsLoadedCorrectlyQuery}
											workspaceUid={workspaceUid}
										/>
									)
								}
								crumbs={[
									...crumbs.portfolioStudio(
										summary?.reference ? PortfolioStudioTab.References : PortfolioStudioTab.Portfolios,
										summary?.uuid,
									),
									...workspacePath,
									...(isEnhanceSelected
										? crumbs.enhancePortfolio({
												uuid: summary.uuid,
												name: summary.name,
												proposalDate: summary.lastActionDate,
												workspaceUuid: workspaceUid,
											})
										: summary?.reference
											? crumbs.referenceName(summary)
											: crumbs.portfolioName({
													...summary,
													workspaceUuid: workspaceUid,
												})),
								]}
								subTitle={isHistoryMode ? undefined : () => <PortfolioSubtitle summary={summary} setAsOf={setAsOf} />}
								bottomBar={
									isHistoryMode ? (
										<AsOfBar
											asOf={asOf}
											setAsOf={setAsOf}
											snapshot={asOfSnapshot}
											inceptionDate={summary.creationDate}
										/>
									) : undefined
								}
							/>

							{summary.status === "RETRIEVING_DATA" ? (
								<Overview
									gridName={gridName}
									isEnhanceSelected={isEnhanceSelected}
									portfolio={summary}
									asOf={asOf}
									handleRef={gridHandleRef}
								/>
							) : (
								<TabGroup
									palette="primary"
									tabIndex={currentTab}
									onTabChange={setCurrentTab}
									data-qualifier="PortfolioDetails/TabGroup"
									headerChildren={
										<ComparativeBenchmark
											uuid={summary.uuid}
											value={selectedComparativeBenchmark}
											onChange={setSelectedComparativeBenchmark}
											canAddBenchmark={
												asOf == null && !["CALCULATING", "RETRIEVING_DATA", "REVIEW"].includes(summary?.status ?? "")
											}
											canSelectBenchmark={asOf == null}
										/>
									}
								>
									{tabs.map(([_, { component }], i) => (
										<Fragment key={i}>{component(currentTab === i, summary)}</Fragment>
									))}
								</TabGroup>
							)}
						</>
					);
				}}
			</EditPortfolioV4ContextProvider>
		</PortfolioContext.Provider>
	);
};

/** @internal — exported for tests */
export function getAvailableTabs({
	gridName,
	isEnhanceSelected,
	isHistoryMode,
	asOf,
	user,
	gridHandleRef,
}: {
	gridName: GridName;
	isEnhanceSelected: boolean;
	/** The page is pinned to a past date (`?asOf`); tabs that only make sense for the live portfolio are hidden. */
	isHistoryMode: boolean;
	/** The validated `?asOf` date, when `isHistoryMode`. */
	asOf?: string;
	user: IUser;
	gridHandleRef: RefObject<WidgetGridHandles | null>;
}) {
	return {
		OVERVIEW: {
			component: (active: boolean, summary: InvestmentSummaryWithRequiredUuid) => (
				<Tab
					title={(props) => (
						<Sandwich>
							<DefaultTabTitle {...props} className="relative pr-6">
								Overview
							</DefaultTabTitle>
							<IconButton
								color={themeCSSVars.palette_N500}
								className="absolute right-0 top-1/2 -translate-y-1/2 p-0!"
								icon="Settings"
								onClick={() => gridHandleRef.current?.openOrderPanel()}
								disabled={!active}
							/>
						</Sandwich>
					)}
					data-qualifier={qualifier.portfolioDetails.tab("Overview")}
				>
					{active && (
						<Overview
							gridName={gridName}
							handleRef={gridHandleRef}
							portfolio={summary}
							isEnhanceSelected={isEnhanceSelected}
							asOf={asOf}
						/>
					)}
				</Tab>
			),
			hasAccess: true,
		},
		COMPOSITION: {
			component: (active: boolean, summary: InvestmentSummaryWithRequiredUuid) => (
				<Tab title="Composition" data-qualifier={qualifier.portfolioDetails.tab("Composition")}>
					{active && <PortfolioComposition enhanced={isEnhanceSelected} portfolio={summary} />}
				</Tab>
			),
			hasAccess: true,
		},
		COMMENTARY: {
			component: (active: boolean, summary: InvestmentSummaryWithRequiredUuid) => (
				<Tab title="Commentary" data-qualifier={qualifier.portfolioDetails.tab("Commentary")}>
					{active ? <PortfolioCommentary enhanced={isEnhanceSelected} portfolio={summary} /> : <></>}
				</Tab>
			),
			hasAccess: hasAccess(user, {
				requiredService: "PORTFOLIO_STUDIO_COMMENTARY_TAB",
			}),
		},
		SCENARIO_ANALYSIS: {
			component: (active: boolean) => (
				<Tab title="Scenario Analysis" data-qualifier={qualifier.portfolioDetails.tab("Scenario Analysis")}>
					{active && <WidgetsMapper style={{ maxHeight: "none" }} widgetName="ScenarioAnalysisEditable" span={3} />}
				</Tab>
			),
			// Scenario analysis is a what-if run against the live composition, so there is no sense in which
			// it can be date-pinned: hidden, not disabled (R13), like the settings tab below.
			hasAccess: !isHistoryMode,
		},
		PORTFOLIO_STUDIO_SETTINGS: {
			component: (active: boolean) => (
				<Tab title="Portfolio Settings" data-qualifier={qualifier.portfolioDetails.tab("Portfolio Settings")}>
					{active && (
						<div className="min-h-0 min-w-0 flex-1 flex h-[calc(100dvh_-_180px)] overflow-hidden">
							<EditPortfolioV4InContext />
						</div>
					)}
				</Tab>
			),
			// Settings describe the live portfolio and cannot be date-pinned, so a past-date view hides the
			// tab outright — hidden, not disabled (R13).
			hasAccess: !isHistoryMode,
		},
	} satisfies Record<
		PortfolioDetailsTabs,
		{
			component: (...args: any) => React.JSX.Element;
			hasAccess: boolean;
		}
	>;
}

export default PortfolioDetails;
